export const TOUR_STEPS = [
  { id: "chat", key: "chat", home: true },
  { id: "chips", key: "chips", home: true },
  { id: "cards", key: "cards", home: true },
  { id: "clients", key: "clients" },
  { id: "docs", key: "docs" },
  { id: "portal", key: "portal" },
  { id: "orders", key: "orders" },
  { id: "quant-admin", key: "quantAdmin", superuser: true },
  { id: "admin", key: "admin", superuser: true },
  { id: "fa", key: "fa", superuser: true },
  { id: "menu", key: "menu" },
  { id: "rail", key: "rail" },
] as const satisfies readonly TourStepSpec[];

export type TourStep = (typeof TOUR_STEPS)[number];

export type TourStepId = TourStep["id"];

export const TOUR_STEP_IDS: readonly TourStepId[] = TOUR_STEPS.map((step) => step.id);

export const SUPERUSER_STEP_IDS: readonly TourStepId[] = TOUR_STEPS.filter(
  (step) => "superuser" in step,
).map((step) => step.id);

export function tourStepsFor(superuser: boolean): TourStep[] {
  return TOUR_STEPS.filter((step) => superuser || !("superuser" in step));
}

export function shownSteps(steps: readonly TourStep[]): TourStep[] {
  return steps.filter((step) => tourTarget(step.id) !== null);
}

export function whenTargetShown(
  id: string,
  signal?: AbortSignal,
  within = TARGET_WAIT_MS,
): Promise<boolean> {
  return new Promise((resolve) => {
    const until = Date.now() + within;
    let timer: number | undefined;
    signal?.addEventListener("abort", stop, { once: true });
    function look() {
      if (signal?.aborted) settle(false);
      else if (tourTarget(id)) timer = window.setTimeout(() => settle(true), SETTLE_MS);
      else if (Date.now() >= until) settle(false);
      else timer = window.setTimeout(look, POLL_MS);
    }
    function stop() {
      window.clearTimeout(timer);
      resolve(false);
    }
    function settle(found: boolean) {
      signal?.removeEventListener("abort", stop);
      resolve(found);
    }
    look();
  });
}

export function tourTarget(id: string): HTMLElement | null {
  const element = document.querySelector<HTMLElement>(`[data-tour="${id}"]`);
  if (!element) return null;
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0 ? element : null;
}

interface TourStepSpec {
  id: string;
  key: string;
  home?: true;
  superuser?: true;
}

const TARGET_WAIT_MS = 2_000;

const POLL_MS = 50;

const SETTLE_MS = 60;
